import { Search } from 'lucide-react'
import { useDeferredValue, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { MetricStrip } from '../components/MetricStrip'
import { Pagination } from '../components/Pagination'
import { TrendChart } from '../components/TrendChart'
import { useCompanyProductOverrides, useInternalProductInfo, usePinduoduoAfterSalesFacts, usePinduoduoProductCatalog, useStoreCostProfiles } from '../platform'
import {
  sumPddAfterSalesMetrics,
  type PddAfterSalesDailyCategoryRecord,
  type PddAfterSalesDailyProductRecord,
  type PddAfterSalesDailyReasonRecord,
  type PddAfterSalesDailyStatusRecord,
  type PddAfterSalesMetrics,
} from '../pinduoduoAfterSales'
import { estimatePinduoduoSecondReturnLoss } from '../pinduoduoSecondReturnLoss'
import {
  PinduoduoStatisticsBar,
  pinduoduoStatisticsSearch,
  pinduoduoStatisticsWindow,
  usePinduoduoStatisticsPeriod,
} from '../pinduoduoStatistics'
import { integer, money } from '../utils'

type TrendKey = 'applicationCount' | 'unshippedCount' | 'shippedCount' | 'successfulRefundCount' | 'successfulRefundAmount' | 'returnRefundCount' | 'qualityIssueCount'
type ProductSortKey = 'applicationCount' | 'unshippedCount' | 'shippedCount' | 'successfulRefundAmount' | 'returnRefundCount' | 'qualityIssueCount'

interface ProductAggregate extends PddAfterSalesMetrics {
  productId: string
  skuExamples: string[]
}

const trendOptions: Record<TrendKey, { label: string; formatter: (value: number) => string }> = {
  applicationCount: { label: '售后申请', formatter: integer },
  unshippedCount: { label: '一退', formatter: integer },
  shippedCount: { label: '二退', formatter: integer },
  successfulRefundCount: { label: '成功退款单', formatter: integer },
  successfulRefundAmount: { label: '成功退款金额', formatter: money },
  returnRefundCount: { label: '退货退款', formatter: integer },
  qualityIssueCount: { label: '品质原因售后', formatter: integer },
}

const share = (value: number, total: number) => total ? `${(value / total * 100).toFixed(1)}%` : '—'

function groupMetrics<T extends PddAfterSalesMetrics>(rows: T[], keyOf: (row: T) => string) {
  const grouped = new Map<string, T[]>()
  for (const row of rows) {
    const key = keyOf(row)
    const group = grouped.get(key)
    if (group) group.push(row)
    else grouped.set(key, [row])
  }
  return [...grouped].map(([key, group]) => ({ key, metrics: sumPddAfterSalesMetrics(group) }))
}

export function PinduoduoAfterSalesPage() {
  const facts = usePinduoduoAfterSalesFacts()
  const catalog = usePinduoduoProductCatalog()
  const internalProducts = useInternalProductInfo()
  const companyOverrides = useCompanyProductOverrides()
  const storeCostProfiles = useStoreCostProfiles()
  const [statisticsPeriod, setStatisticsPeriod, customRange, setCustomRange] = usePinduoduoStatisticsPeriod('day')
  const [trendKey, setTrendKey] = useState<TrendKey>('applicationCount')
  const [search, setSearch] = useState('')
  const deferredSearch = useDeferredValue(search)
  const [sortKey, setSortKey] = useState<ProductSortKey>('applicationCount')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const window = useMemo(
    () => pinduoduoStatisticsWindow(facts.data, statisticsPeriod, customRange),
    [customRange.endDate, customRange.startDate, facts.data, statisticsPeriod],
  )
  const dateInWindow = (date: string) => Boolean(window && date >= window.startDate && date <= window.endDate)
  const daily = useMemo(() => (facts.data?.daily || []).filter((row) => dateInWindow(row.date)), [facts.data, window?.endDate, window?.startDate])
  const metrics = useMemo(() => sumPddAfterSalesMetrics(daily), [daily])
  const dailySkus = useMemo(() => (facts.data?.dailySkus || []).filter((row) => dateInWindow(row.date)), [facts.data, window?.endDate, window?.startDate])
  const trend = useMemo(() => daily.map((row) => ({ date: row.date, value: row[trendKey] })), [daily, trendKey])
  const categoryRows = useMemo(() => groupMetrics(
    (facts.data?.dailyCategories || []).filter((row) => dateInWindow(row.date)),
    (row: PddAfterSalesDailyCategoryRecord) => row.category,
  ).sort((left, right) => right.metrics.applicationCount - left.metrics.applicationCount), [facts.data, window?.endDate, window?.startDate])
  const statusRows = useMemo(() => groupMetrics(
    (facts.data?.dailyStatuses || []).filter((row) => dateInWindow(row.date)),
    (row: PddAfterSalesDailyStatusRecord) => row.status,
  ).sort((left, right) => right.metrics.applicationCount - left.metrics.applicationCount), [facts.data, window?.endDate, window?.startDate])
  const reasonRows = useMemo(() => groupMetrics(
    (facts.data?.dailyReasons || []).filter((row) => dateInWindow(row.date)),
    (row: PddAfterSalesDailyReasonRecord) => row.reason,
  ).sort((left, right) => right.metrics.applicationCount - left.metrics.applicationCount), [facts.data, window?.endDate, window?.startDate])
  const productNames = useMemo(() => new Map((catalog.data?.products || []).map((product) => [product.productId, product.name])), [catalog.data])
  const skuCodeByVariantId = useMemo(() => new Map((catalog.data?.variants || []).map((variant) => [
    variant.variantId, variant.merchantVariantCode.trim(),
  ])), [catalog.data])
  const companyRecordsByCode = useMemo(() => {
    const result = new Map<string, NonNullable<typeof internalProducts.data>['records']>()
    for (const record of internalProducts.data?.records || []) {
      const records = result.get(record.productCode.trim()) || []
      records.push(record)
      result.set(record.productCode.trim(), records)
    }
    return result
  }, [internalProducts.data])
  const companyOverridesByCode = useMemo(() => new Map(
    (companyOverrides.data?.records || []).map((record) => [record.companyVariantId.trim(), record]),
  ), [companyOverrides.data])
  const storeCostProfile = storeCostProfiles.data?.stores.find((item) =>
    item.platformId === 'pinduoduo' && item.storeId === facts.data?.meta.storeId,
  )
  const secondReturnLoss = useMemo(() => estimatePinduoduoSecondReturnLoss(
    daily, dailySkus, skuCodeByVariantId, storeCostProfile, companyRecordsByCode, companyOverridesByCode,
  ), [companyOverridesByCode, companyRecordsByCode, daily, dailySkus, skuCodeByVariantId, storeCostProfile])
  const products = useMemo<ProductAggregate[]>(() => {
    const rows = (facts.data?.dailyProducts || []).filter((row) => dateInWindow(row.date))
    const grouped = new Map<string, PddAfterSalesDailyProductRecord[]>()
    for (const row of rows) {
      const group = grouped.get(row.productId)
      if (group) group.push(row)
      else grouped.set(row.productId, [row])
    }
    return [...grouped].map(([productId, group]) => ({
      productId,
      skuExamples: [...new Set(group.flatMap((row) => row.skuExamples))].slice(0, 3),
      ...sumPddAfterSalesMetrics(group),
    }))
  }, [facts.data, window?.endDate, window?.startDate])
  const filteredProducts = useMemo(() => {
    const term = deferredSearch.trim().toLocaleLowerCase('zh-CN')
    return products.filter((product) => {
      if (!term) return true
      return [product.productId, productNames.get(product.productId), ...product.skuExamples]
        .filter(Boolean).join(' ').toLocaleLowerCase('zh-CN').includes(term)
    }).sort((left, right) => right[sortKey] - left[sortKey] || right.successfulRefundAmount - left.successfulRefundAmount)
  }, [deferredSearch, productNames, products, sortKey])
  const pageCount = Math.max(1, Math.ceil(filteredProducts.length / pageSize))
  const currentPage = Math.min(page, pageCount)
  const visibleProducts = filteredProducts.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  if (facts.loading || catalog.loading || internalProducts.loading || companyOverrides.loading || storeCostProfiles.loading) return <div className="loading-state">正在加载拼多多售后与成本数据…</div>
  const costError = catalog.error || internalProducts.error || companyOverrides.error || storeCostProfiles.error
    || (storeCostProfiles.data && !storeCostProfile ? '未找到当前拼多多店铺成本配置' : '')
  if (facts.error || !facts.data || costError) return <div className="error-state"><h1>拼多多售后数据加载失败</h1><p>{facts.error || costError}</p><button type="button" className="primary-button" onClick={() => void facts.reload()}>重试</button></div>
  if (!window) return <div className="error-state"><h1>拼多多售后日期范围不可用</h1></div>
  const selectedTrend = trendOptions[trendKey]
  const productSearch = pinduoduoStatisticsSearch(statisticsPeriod, customRange)

  return <div className="page-stack pdd-page pdd-after-sales-page">
    <div className="page-title-row">
      <div>
        <span className="pdd-platform-label">PINDUODUO AFTER-SALES</span>
        <h1>退货与售后分析</h1>
        <p>{facts.data.meta.storeName} · 售后工作台申请记录聚合</p>
      </div>
      <div className="page-title-actions">
        <DataFreshnessBadge date={facts.data.meta.queryEnd} label="售后数据截至" mode="on-demand" />
        <Link className="text-link" to="/pinduoduo">返回店铺总览</Link>
      </div>
    </div>

    <div className="pdd-caliber-note">
      <strong>口径边界：</strong>一行代表一个售后单，日期取申请时间；一退是申请时未发货，二退是申请时已发货，两者互斥。一退/二退占比以售后申请为分母，不是销售退款率。二退损失按订单去重：往返运费＝店铺运费×2，包装损耗＝1元/单，预计货损＝已识别产品成本×5%。退回商品默认重新入库再售，不扣整件产品成本，只估部分折损；一退不计损失。订单号只在导入时去重，不保存。实际退货运费和商品损坏结算尚未取得，因此仍为经营估算。
    </div>

    <PinduoduoStatisticsBar
      period={statisticsPeriod}
      onChange={setStatisticsPeriod}
      window={window}
      customRange={customRange}
      onCustomRangeChange={setCustomRange}
      title="售后申请统计范围"
      ariaLabel="拼多多售后申请统计范围"
      customAriaLabel="自定义售后申请日期"
    />

    <MetricStrip items={[
      { label: '售后申请', value: integer(metrics.applicationCount), context: '售后单数', active: trendKey === 'applicationCount', onClick: () => setTrendKey('applicationCount') },
      { label: '一退', value: integer(metrics.unshippedCount), context: `占申请 ${share(metrics.unshippedCount, metrics.applicationCount)}`, active: trendKey === 'unshippedCount', onClick: () => setTrendKey('unshippedCount') },
      { label: '二退', value: integer(metrics.shippedCount), context: `占申请 ${share(metrics.shippedCount, metrics.applicationCount)}`, active: trendKey === 'shippedCount', onClick: () => setTrendKey('shippedCount') },
      { label: secondReturnLoss.complete ? '二退损失' : '已覆盖二退损失', value: money(secondReturnLoss.totalLoss), context: `${integer(secondReturnLoss.secondReturnOrderCount)}单 · 货损覆盖 ${integer(secondReturnLoss.coveredDamageItemCount)} / ${integer(secondReturnLoss.secondReturnItemCount)}件` },
      { label: '成功退款单', value: integer(metrics.successfulRefundCount), context: `占申请 ${share(metrics.successfulRefundCount, metrics.applicationCount)}`, active: trendKey === 'successfulRefundCount', onClick: () => setTrendKey('successfulRefundCount') },
      { label: '成功退款金额', value: money(metrics.successfulRefundAmount), context: '退款成功记录', active: trendKey === 'successfulRefundAmount', onClick: () => setTrendKey('successfulRefundAmount') },
      { label: '退货退款', value: integer(metrics.returnRefundCount), context: `占申请 ${share(metrics.returnRefundCount, metrics.applicationCount)}`, active: trendKey === 'returnRefundCount', onClick: () => setTrendKey('returnRefundCount') },
      { label: '仅退款', value: integer(metrics.refundOnlyCount), context: `占申请 ${share(metrics.refundOnlyCount, metrics.applicationCount)}` },
      { label: '换货 / 补寄', value: integer(metrics.exchangeResendCount), context: '不计成功退款金额' },
      { label: '品质原因售后', value: integer(metrics.qualityIssueCount), context: `占申请 ${share(metrics.qualityIssueCount, metrics.applicationCount)}`, active: trendKey === 'qualityIssueCount', onClick: () => setTrendKey('qualityIssueCount') },
      { label: '当前待处理', value: integer(metrics.pendingCount), context: '以导出时状态为准' },
    ]} />

    <section className="overview-section pdd-trend-section">
      <div className="section-heading-row"><div><h2>{window.label}{selectedTrend.label}每日趋势</h2><p>点击上方指标卡切换；无申请日期显示0</p></div></div>
      <TrendChart data={trend} valueFormatter={selectedTrend.formatter} valueLabel={selectedTrend.label} ariaLabel={`拼多多${window.label}${selectedTrend.label}每日趋势`} />
    </section>

    <section className="overview-section">
      <div className="section-heading-row"><div><h2>二退成本拆解</h2><p>退回默认可再入库销售；只计往返履约与部分折损，不扣整件产品成本</p></div><span>{integer(secondReturnLoss.secondReturnOrderCount)} 单</span></div>
      <div className="table-responsive"><table className="pdd-table"><thead><tr><th>项目</th><th>规则</th><th>覆盖</th><th>金额</th></tr></thead><tbody>
        <tr><td><strong>往返运费</strong></td><td>店铺运费 × {secondReturnLoss.freightMultiplier?.toFixed(0) || '—'}</td><td>{integer(secondReturnLoss.coveredOrderCount)} / {integer(secondReturnLoss.secondReturnOrderCount)} 单</td><td>{money(secondReturnLoss.roundTripFreightCost)}</td></tr>
        <tr><td><strong>包装损耗</strong></td><td>当前生效包装成本，历史统一为1元/单</td><td>{integer(secondReturnLoss.coveredOrderCount)} / {integer(secondReturnLoss.secondReturnOrderCount)} 单</td><td>{money(secondReturnLoss.packagingLoss)}</td></tr>
        <tr><td><strong>预计产品货损</strong></td><td>可再售前提下，产品成本 × {secondReturnLoss.damageRate?.toFixed(0) || '—'}% 折损；不扣整件成本</td><td>{integer(secondReturnLoss.coveredDamageItemCount)} / {integer(secondReturnLoss.secondReturnItemCount)} 件</td><td>{money(secondReturnLoss.expectedDamageCost)}</td></tr>
        <tr className="calc-major"><td><strong>＝ {secondReturnLoss.complete ? '二退损失' : '已覆盖二退损失'}</strong></td><td>往返运费＋包装损耗＋预计产品货损</td><td>{secondReturnLoss.complete ? '成本完整' : '缺产品成本的货损暂空'}</td><td><strong>{money(secondReturnLoss.totalLoss)}</strong></td></tr>
      </tbody></table></div>
    </section>

    <div className="pdd-after-sales-two-column">
      <section className="overview-section">
        <div className="section-heading-row"><div><h2>售后分类</h2><p>默认沿用平台退款类型；`换货补寄待用户处理` 单独成类，仍与已发货阶段计入二退观察</p></div></div>
        <div className="table-responsive"><table className="pdd-table"><thead><tr><th>分类</th><th>申请</th><th>占比</th><th>成功退款</th><th>成功退款金额</th></tr></thead><tbody>{categoryRows.map((row) => <tr key={row.key}><td><strong>{row.key}</strong></td><td>{integer(row.metrics.applicationCount)}</td><td>{share(row.metrics.applicationCount, metrics.applicationCount)}</td><td>{integer(row.metrics.successfulRefundCount)}</td><td>{money(row.metrics.successfulRefundAmount)}</td></tr>)}</tbody></table></div>
      </section>
      <section className="overview-section">
        <div className="section-heading-row"><div><h2>当前状态</h2><p>状态是本次导出时点快照，后续可能变化</p></div></div>
        <div className="table-responsive"><table className="pdd-table"><thead><tr><th>状态</th><th>售后单</th><th>占比</th><th>成功退款金额</th></tr></thead><tbody>{statusRows.map((row) => <tr key={row.key}><td><strong>{row.key}</strong></td><td>{integer(row.metrics.applicationCount)}</td><td>{share(row.metrics.applicationCount, metrics.applicationCount)}</td><td>{row.metrics.successfulRefundAmount ? money(row.metrics.successfulRefundAmount) : '—'}</td></tr>)}</tbody></table></div>
      </section>
    </div>

    <section className="overview-section">
      <div className="section-heading-row"><div><h2>退款原因</h2><p>按售后申请数降序；“其他原因”仍保留原平台口径，不猜测细分</p></div><span>{integer(reasonRows.length)} 类原因</span></div>
      <div className="table-responsive"><table className="pdd-table"><thead><tr><th>退款原因</th><th>申请</th><th>占比</th><th>成功退款单</th><th>成功退款金额</th><th>一退</th><th>二退</th></tr></thead><tbody>{reasonRows.map((row) => <tr key={row.key}><td><strong>{row.key}</strong></td><td>{integer(row.metrics.applicationCount)}</td><td>{share(row.metrics.applicationCount, metrics.applicationCount)}</td><td>{integer(row.metrics.successfulRefundCount)}</td><td>{money(row.metrics.successfulRefundAmount)}</td><td>{integer(row.metrics.unshippedCount)}</td><td>{integer(row.metrics.shippedCount)}</td></tr>)}</tbody></table></div>
    </section>

    <section className="overview-section">
      <div className="section-heading-row"><div><h2>商品售后排行</h2><p>按平台商品ID聚合；点击商品进入对应SPU经营详情</p></div><span>命中 {integer(filteredProducts.length)} 个商品</span></div>
      <div className="filter-bar">
        <label className="search-control"><Search aria-hidden="true" /><span className="sr-only">搜索售后商品</span><input value={search} placeholder="搜索商品名、商品ID、SKU名称" onChange={(event) => { setSearch(event.target.value); setPage(1) }} /></label>
        <label><span className="sr-only">商品排序</span><select value={sortKey} onChange={(event) => { setSortKey(event.target.value as ProductSortKey); setPage(1) }}><option value="applicationCount">按售后申请降序</option><option value="unshippedCount">按一退降序</option><option value="shippedCount">按二退降序</option><option value="successfulRefundAmount">按成功退款金额降序</option><option value="returnRefundCount">按退货退款降序</option><option value="qualityIssueCount">按品质原因降序</option></select></label>
      </div>
      <div className="table-responsive"><table className="pdd-table pdd-after-sales-product-table"><thead><tr><th>商品</th><th>售后申请</th><th>一退</th><th>一退占比</th><th>二退</th><th>二退占比</th><th>成功退款单</th><th>成功退款金额</th><th>退货退款</th><th>仅退款</th><th>品质原因</th></tr></thead><tbody>{visibleProducts.map((product) => <tr key={product.productId}>
        <td><Link className="text-link" to={`/pinduoduo/spu/${encodeURIComponent(product.productId)}${productSearch}`}><strong>{productNames.get(product.productId) || product.skuExamples[0] || `商品 ${product.productId}`}</strong></Link><small>商品ID {product.productId}{product.skuExamples.length ? ` · ${product.skuExamples.join(' / ')}` : ''}</small></td>
        <td>{integer(product.applicationCount)}</td><td>{integer(product.unshippedCount)}</td><td>{share(product.unshippedCount, product.applicationCount)}</td><td>{integer(product.shippedCount)}</td><td>{share(product.shippedCount, product.applicationCount)}</td><td>{integer(product.successfulRefundCount)}</td><td>{money(product.successfulRefundAmount)}</td><td>{integer(product.returnRefundCount)}</td><td>{integer(product.refundOnlyCount)}</td><td>{integer(product.qualityIssueCount)}</td>
      </tr>)}</tbody></table></div>
      <Pagination page={currentPage} pageCount={pageCount} total={filteredProducts.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1) }} />
    </section>
  </div>
}
